import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

const RETRY_KEY = "eb_chunk_retry";

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    const isChunkError =
      error.name === "ChunkLoadError" ||
      error.message?.includes("Failed to fetch dynamically imported module") ||
      error.message?.includes("Loading chunk");

    if (isChunkError && !sessionStorage.getItem(RETRY_KEY)) {
      sessionStorage.setItem(RETRY_KEY, "1");
      window.location.reload();
      return;
    }

    // Clear retry flag on non-chunk errors so future chunk errors can retry
    sessionStorage.removeItem(RETRY_KEY);
  }

  handleReload = () => {
    sessionStorage.removeItem(RETRY_KEY);
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-5 px-6 text-center">
          <div
            className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-coral flex items-center justify-center"
          >
            <svg viewBox="0 0 24 24" className="w-7 h-7 stroke-white fill-none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 9a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V9Z"/>
              <path d="M9 6v12"/>
              <circle cx="15" cy="12" r="1.5"/>
            </svg>
          </div>
          <span className="font-display text-2xl font-bold text-foreground tracking-tight">E-tickets</span>
          <p className="text-sm text-muted-foreground max-w-xs">
            La page n'a pas pu se charger. Vérifiez votre connexion et réessayez.
          </p>
          <button
            onClick={this.handleReload}
            className="mt-2 px-6 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition-opacity"
          >
            Recharger
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
